import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { buildProductHierarchy } from 'src/app/_helpers/product-levels';
import { ProductDetails } from 'src/app/_types/proposals.type';
import { LanguageCode } from 'src/app/helpers/nationalities';

type ProductWithChildren = ProductDetails & {
  products: ProductWithChildren[];
  displayName?: string;
};

@Component({
  selector: 'app-proposal-products',
  templateUrl: './proposal-products-displayer.component.html',
  styleUrls: ['./proposal-products-displayer.component.css'],
  standalone: true,
  imports: [CommonModule],
})
export class ProposalProductsDisplayerComponent implements OnChanges {
  @Input()
  productDetailsList: ProductDetails[] = [];

  @Input()
  comparisonProductDetailsList?: ProductDetails[] | null;

  hierarchicalProducts: ProductWithChildren[] = [];

  comparisonHierarchicalProducts: ProductWithChildren[] = [];

  hasComparisonDiff = false;

  lngCode: LanguageCode;

  private readonly productDisplayNames: Map<string, string> = new Map();

  private readonly comparisonProductDisplayNames: Map<string, string> =
    new Map();

  constructor(private readonly cookieService: CookieService) {
    this.lngCode =
      (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['productDetailsList'] ||
      changes['comparisonProductDetailsList']
    ) {
      // Build the display model for the "new/current" list.
      this.hierarchicalProducts = this.buildDisplayableHierarchy(
        this.productDetailsList,
        this.productDisplayNames
      );

      // Build the optional "old/baseline" list used in diff mode.
      this.comparisonHierarchicalProducts = this.buildDisplayableHierarchy(
        this.comparisonProductDetailsList ?? [],
        this.comparisonProductDisplayNames
      );

      // Toggle diff styling only when both lists are effectively different.
      this.hasComparisonDiff = this.hasProductsDiff(
        this.productDetailsList,
        this.comparisonProductDetailsList
      );
    }
  }

  /**
   * Processes products to append sub-product names to main products.
   * When a product has mainProductId !== rootProductId and amount === 0,
   * its name is appended to the main product's display name.
   */
  private processProductNames(
    products: ProductDetails[],
    displayNameMap: Map<string, string>
  ): void {
    displayNameMap.clear();

    // Create a map of products by their productId for quick lookup
    const productMap = new Map<string, ProductDetails>();
    products.forEach((product) => {
      productMap.set(product.productId, product);
    });

    // Group sub-products by their mainProductId
    const productsByMainProduct = new Map<string, ProductDetails[]>();

    products.forEach((product) => {
      // Check if this is a sub-product (mainProductId !== rootProductId) with amount === 0
      if (
        product.mainProductId &&
        product.rootProductId &&
        product.mainProductId !== product.rootProductId &&
        product.amount === 0
      ) {
        if (!productsByMainProduct.has(product.mainProductId)) {
          productsByMainProduct.set(product.mainProductId, []);
        }
        const productGroup = productsByMainProduct.get(product.mainProductId);
        if (productGroup) {
          productGroup.push(product);
        }
      }
    });

    // Update main product display names with appended sub-product names
    // Only use the first sub-product if multiple exist (shouldn't happen, but handle gracefully)
    productsByMainProduct.forEach((subProducts, mainProductId) => {
      const mainProduct = productMap.get(mainProductId);
      if (mainProduct && subProducts.length > 0) {
        // Start with the original main product name
        let displayName =
          this.lngCode === 'en'
            ? (mainProduct.productName ?? mainProduct.productNameInFrench)
            : (mainProduct.productNameInFrench ?? mainProduct.productName);

        // Only append the first sub-product name (only one subproduct per main product allowed)
        const firstSubProduct = subProducts[0];
        const subProductName =
          this.lngCode === 'en'
            ? (firstSubProduct.productName ??
              firstSubProduct.productNameInFrench)
            : (firstSubProduct.productNameInFrench ??
              firstSubProduct.productName);
        displayName = `${displayName} - ${subProductName}`;

        displayNameMap.set(mainProductId, displayName);
      }
    });

    // Set default display names for all products
    products.forEach((product) => {
      if (!displayNameMap.has(product.productId)) {
        const defaultName =
          this.lngCode === 'en'
            ? (product.productName ?? product.productNameInFrench)
            : (product.productNameInFrench ?? product.productName);
        displayNameMap.set(product.productId, defaultName);
      }
    });
  }

  /**
   * Recursively filters out products that shouldn't be displayed and sets display names.
   * Products with mainProductId !== rootProductId and amount === 0 are filtered out.
   */
  private filterDisplayableProducts(
    products: ProductWithChildren[],
    displayNameMap: Map<string, string>
  ): ProductWithChildren[] {
    return products
      .filter((product) => {
        // Filter out products that should be hidden
        if (
          product.mainProductId &&
          product.rootProductId &&
          product.mainProductId !== product.rootProductId &&
          product.amount === 0
        ) {
          return false;
        }
        return true;
      })
      .map((product) => {
        // Create a copy of the product
        const filteredProduct: ProductWithChildren = { ...product };

        // Set display name if available (for products with appended sub-product names)
        if (displayNameMap.has(product.productId)) {
          filteredProduct.displayName = displayNameMap.get(product.productId)!;
        }

        // Recursively filter and process children
        if (product.products && product.products.length > 0) {
          filteredProduct.products = this.filterDisplayableProducts(
            product.products,
            displayNameMap
          );
        }

        return filteredProduct;
      });
  }

  /**
   * Checks if a product should be displayed.
   * Products with mainProductId !== rootProductId and amount === 0 should not be displayed.
   */
  shouldDisplayProduct(product: ProductDetails): boolean {
    if (
      product.mainProductId &&
      product.rootProductId &&
      product.mainProductId !== product.rootProductId &&
      product.amount === 0
    ) {
      return false;
    }
    return true;
  }

  getProductName(product: ProductWithChildren): string {
    // Use display name if available (for products with appended sub-product names)
    if (product.displayName) {
      return product.displayName;
    }

    return this.lngCode === 'en'
      ? (product.productName ?? product.productNameInFrench ?? '')
      : (product.productNameInFrench ?? product.productName ?? '');
  }

  private buildDisplayableHierarchy(
    products: ProductDetails[] | null | undefined,
    displayNameMap: Map<string, string>
  ): ProductWithChildren[] {
    const productList = products ?? [];

    if (productList.length === 0) {
      displayNameMap.clear();
      return [];
    }

    // Helper returns a tree of product nodes; we enrich it with display names and filtering.
    const hierarchy = buildProductHierarchy(
      productList
    ) as unknown as ProductWithChildren[];
    this.processProductNames(productList, displayNameMap);
    const filtered = this.filterDisplayableProducts(hierarchy, displayNameMap);

    filtered.sort((a, b) => {
      const aOrder = a.coverOrder && a.coverOrder > 0 ? a.coverOrder : Infinity;
      const bOrder = b.coverOrder && b.coverOrder > 0 ? b.coverOrder : Infinity;
      return aOrder - bOrder;
    });

    return filtered;
  }

  private hasProductsDiff(
    currentProducts: ProductDetails[] | null | undefined,
    previousProducts: ProductDetails[] | null | undefined
  ): boolean {
    // No baseline means no comparison mode.
    if (!previousProducts) {
      return false;
    }

    const currentSignatures = this.buildProductSignatures(
      currentProducts ?? []
    );
    const previousSignatures = this.buildProductSignatures(previousProducts);

    if (currentSignatures.length !== previousSignatures.length) {
      return true;
    }

    return currentSignatures.some(
      (signature, index) => signature !== previousSignatures[index]
    );
  }

  private buildProductSignatures(products: ProductDetails[]): string[] {
    // Compare stable, normalized signatures instead of object identity/reference.
    return products
      .map((product) =>
        [
          product.productId ?? '',
          product.mainProductId ?? '',
          product.rootProductId ?? '',
          product.productName ?? '',
          product.productNameInFrench ?? '',
          product.amount ?? '',
        ].join('|')
      )
      .sort((a, b) => a.localeCompare(b));
  }
}
